import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useController, useFormContext, type Control, type FieldPath, type FieldValues } from 'react-hook-form'
import {
  ComboBox,
  Input,
  Button,
  Popover,
  ListBox,
  ListBoxItem,
  Label,
  FieldError,
  Text,
} from 'react-aria-components'
import { ChevronDownIcon, XCloseIcon } from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'

export interface ComboboxOption {
  value: string | number
  label: string
  disabled?: boolean
}

export interface FormComboboxProps<TFieldValues extends FieldValues> {
  control?: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  label?: string
  description?: string
  placeholder?: string
  options: ComboboxOption[]
  isDisabled?: boolean
  isRequired?: boolean
  allowsCustomValue?: boolean
  multiple?: boolean
  className?: string
  /** Callback when the search input value changes (for async filtering) */
  onSearchChange?: (value: string) => void
}

export function FormCombobox<TFieldValues extends FieldValues>({
  control: controlProp,
  name,
  label,
  description,
  placeholder,
  options,
  isDisabled,
  isRequired,
  allowsCustomValue = false,
  multiple = false,
  className,
  onSearchChange,
}: FormComboboxProps<TFieldValues>) {
  const { t } = useTranslation()
  const resolvedPlaceholder = placeholder ?? t('resources.generic.search')

  // Use provided control or get from context
  const formContext = useFormContext<TFieldValues>()
  const control = controlProp ?? formContext?.control

  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name,
  })

  const [inputValue, setInputValue] = useState('')

  // Initialize input value from field value when options are loaded.
  // With allowsCustomValue, a value that matches no option (e.g. restored from the URL) is shown as-is.
  useEffect(() => {
    if (multiple || !field.value || options.length === 0) return
    const option = options.find((opt) => String(opt.value) === String(field.value))
    const display = option?.label ?? (allowsCustomValue ? String(field.value) : null)
    if (display !== null && inputValue !== display) {
      setInputValue(display)
    }
  }, [field.value, options, multiple, inputValue, allowsCustomValue])

  const filteredOptions = useMemo(() => {
    if (!inputValue) return options
    const search = inputValue.toLowerCase()
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(search) ||
        String(option.value).toLowerCase().includes(search)
    )
  }, [options, inputValue])

  // For multiple selection, track selected values as an array
  const selectedValues: (string | number)[] = multiple 
    ? (Array.isArray(field.value) ? field.value : [])
    : []

  const handleSelectionChange = (key: React.Key | null) => {
    if (key) {
      if (multiple) {
        // Toggle selection for multi-select
        const keyValue = typeof key === 'string' && !isNaN(Number(key)) ? Number(key) : key
        const currentValues = Array.isArray(field.value) ? field.value : []
        const isSelected = currentValues.some((v: string | number) => v === keyValue || String(v) === String(key))
        
        if (isSelected) {
          field.onChange(currentValues.filter((v: string | number) => v !== keyValue && String(v) !== String(key)))
        } else {
          field.onChange([...currentValues, keyValue])
        }
        // Clear input after selection in multi-select mode
        setInputValue('')
      } else {
        // Convert key back to number if it was originally a number
        // (React Aria uses string keys, but we want to preserve numeric IDs)
        const keyValue = typeof key === 'string' && !isNaN(Number(key)) ? Number(key) : key
        field.onChange(keyValue)
        const option = options.find((opt) => String(opt.value) === String(key))
        setInputValue(option?.label || '')
      }
    }
  }

  const handleInputChange = (value: string) => {
    setInputValue(value)
    // Notify parent about search value changes (for async filtering)
    onSearchChange?.(value)
    if (allowsCustomValue && !multiple) {
      field.onChange(value)
    }
    // For single-select: clear the field value when input is modified
    // This allows users to clear the selection by typing/backspacing
    if (!multiple && !allowsCustomValue && field.value) {
      // Find the currently selected option's label
      const selectedOption = options.find(
        (opt) => String(opt.value) === String(field.value)
      )
      // If the input no longer matches the selected label, clear the selection
      if (selectedOption && value !== selectedOption.label) {
        field.onChange(null)
      }
    }
  }

  const handleClear = () => {
    if (multiple) {
      field.onChange([])
    } else {
      // Use null for clearing to match nullish schema expectations
      field.onChange(null)
    }
    setInputValue('')
  }

  const handleRemoveTag = (valueToRemove: string | number) => {
    if (multiple) {
      const currentValues = Array.isArray(field.value) ? field.value : []
      field.onChange(currentValues.filter((v: string | number) => v !== valueToRemove))
    }
  }

  const getSelectedLabels = () => {
    if (!multiple) return []
    return selectedValues
      .map(v => options.find(opt => String(opt.value) === String(v))?.label)
      .filter(Boolean) as string[]
  }

  const hasValue = multiple ? selectedValues.length > 0 : !!field.value

  return (
    <ComboBox
      className={cn('flex flex-col gap-1.5', className)}
      isDisabled={isDisabled}
      isRequired={isRequired}
      selectedKey={multiple ? null : (field.value ?? null)}
      onSelectionChange={handleSelectionChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      allowsCustomValue={allowsCustomValue || multiple}
      menuTrigger="focus"
      aria-label={label || resolvedPlaceholder}
    >
      {label && (
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-200">
          {label}
          {isRequired && <span className="text-error-500 ml-0.5">*</span>}
        </Label>
      )}
      <div className="relative">
        {/* Selected tags for multiple mode */}
        {multiple && selectedValues.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {getSelectedLabels().map((label, index) => (
              <span
                key={selectedValues[index]}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-sm bg-brand-50 text-brand-700 rounded-md dark:bg-brand-950 dark:text-brand-300"
              >
                {label}
                <button
                  type="button"
                  onClick={() => handleRemoveTag(selectedValues[index])}
                  className="text-brand-500 hover:text-brand-700 dark:hover:text-brand-200"
                >
                  <XCloseIcon className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <Input
          placeholder={resolvedPlaceholder}
          className={cn(
            'w-full rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 pr-16 text-base',
            'placeholder:text-gray-400',
            'focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-none',
            'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
            'dark:disabled:bg-gray-800 dark:disabled:text-gray-400',
            'dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100',
            'dark:focus:border-brand-400 dark:focus:ring-brand-400',
            error && 'border-error-500 focus:border-error-500 focus:ring-error-500'
          )}
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {hasValue && (
            <Button
              onPress={handleClear}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              iconLeading={XCloseIcon}
            />
          )}
          <Button className="p-1 text-gray-400" iconLeading={ChevronDownIcon} />
        </div>
      </div>
      <Popover
        className={cn(
          'w-[var(--trigger-width)] rounded-lg border border-gray-200 bg-white shadow-lg',
          'dark:bg-gray-900 dark:border-gray-700',
          'entering:animate-in entering:fade-in-0 entering:zoom-in-95',
          'exiting:animate-out exiting:fade-out-0 exiting:zoom-out-95'
        )}
      >
        <ListBox 
          className="p-1 max-h-60 overflow-auto outline-none"
          selectionMode={multiple ? 'multiple' : 'single'}
          selectedKeys={multiple ? new Set(selectedValues.map(v => String(v))) : undefined}
        >
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
              {t('resources.generic.no_results')}
            </div>
          ) : (
            filteredOptions.map((option) => {
              const isSelected = multiple && selectedValues.some(v => String(v) === String(option.value))
              return (
                <ListBoxItem
                  key={String(option.value)}
                  id={String(option.value)}
                  textValue={option.label}
                  isDisabled={option.disabled}
                  className={cn(
                    'flex items-center px-3 py-2 rounded-md text-sm cursor-pointer outline-none',
                    'text-gray-900 dark:text-gray-100',
                    'hover:bg-gray-100 dark:hover:bg-gray-800',
                    'focus:bg-gray-100 dark:focus:bg-gray-800',
                    isSelected && 'bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300',
                    'disabled:text-gray-400 disabled:cursor-not-allowed'
                  )}
                >
                  {option.label}
                </ListBoxItem>
              )
            })
          )}
        </ListBox>
      </Popover>
      {description && !error && (
        <Text slot="description" className="text-sm text-gray-500 dark:text-gray-400">
          {description}
        </Text>
      )}
      {error && (
        <FieldError className="text-sm text-error-600 dark:text-error-400">
          {t(error.message ?? '')}
        </FieldError>
      )}
    </ComboBox>
  )
}

export default FormCombobox
